import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import {
	broadcastChannel,
	type CandidateFileData,
	candidateFileEvent,
	candidateFileIndexQueryKey,
	candidateFileTagDestroy,
	candidateFileTagRequiresCleanScan,
	candidateFileTagUpdate,
	type CandidateFileTagUpdate422,
	candidateProfileEvent,
	candidateProfileShowQueryKey,
	type MediaAttribute,
	pipelineStageShowQueryKey,
	type QuestionWithScoreData,
	stripeFeature,
	useCandidateFileDestroy,
	useCandidateFileIndex,
	useCandidateFileStore,
	useCandidateProfileShow,
	useOrganisationRelationShow,
} from '@jobilla/nexus-sdk'
import ctl from '@netlify/classnames-template-literals'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams, useSearch } from '@tanstack/react-router'
import type { AxiosError } from 'axios'
import { useAtom } from 'jotai'
import {
	ArrowRightIcon,
	CalendarIcon,
	CheckIcon,
	ChevronDownIcon,
	ChevronUpIcon,
	ClipboardListIcon,
	HistoryIcon,
	MessageCircleIcon,
	PaperclipIcon,
	UserIcon,
	VideoIcon,
	XIcon,
} from 'lucide-react'
import { motion, PanInfo } from 'motion/react'
import { ChangeEvent, ReactNode, Ref, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CandidateProfileTab } from '@/features/candidate-profile/candidate-profile-tab'
import { ProfileSectionCounts } from '@/features/candidate-profile/profile-section-counts'
import { activeCallAtom, callStore } from '@/features/interview-scheduling/call-store'
import { InterviewsTab } from '@/features/interview-scheduling/interviews-tab'
import { JobillaMeetTab } from '@/features/interview-scheduling/jobilla-meet-tab'
import { NextInterviewPanel } from '@/features/interview-scheduling/next-interview-panel'
import { SendBookingLinkDialog } from '@/features/interview-scheduling/send-booking-link-dialog'
import { useCandidateEmails } from '@/hooks/use-candidate-emails'
import { useCandidateWhatsApp } from '@/hooks/use-candidate-whatsapp'
import { useEntitlements, usePrivateChannel } from '@/lib'
import { ConfirmationDialog, type ConfirmationDialogRef, logger, PipelineBadge, TimeAgo, Tooltip } from '@/ui'
import { CampaignModel, CandidateModel, LinkLocalProps, type ScoreBreakdownItem } from '@/ui'
import { CandidateAdditionalAnswers } from '@/ui'
import { CandidateFiles, CvFileItem } from '@/ui'
import { EditorErrorBoundary } from '@/ui/editor/error-handling'
import { useWhatsAppEnabled } from '@/ui/utilities/hooks/use-whatsapp-enabled'
import { ApplicationHistory } from './application-history'
import { ApplicationResponseItem, type ScoredOption } from './application-response-item'
import { CandidateInternalNotes } from './candidate-internal-notes'
import { CandidateTabOpenedTracker } from './candidate-tab-opened-tracker'
import { EmailThread } from './email-thread'
import { ScoreBadge } from './score-badge'
import { WhatsAppThread } from './whatsapp-thread'

const PANEL_CONFIG = {
	minPercent: 30,
	maxPercent: 75,
	defaultLeftPercent: 65,
} as const

type LeftPanelTab = 'responses' | 'profile' | 'emails' | 'whatsapp' | 'meeting'
type RightPanelTab = 'overview' | 'interviews' | 'history' | 'files'

type CandidateScore = {
	percentage: number
	total: number
	max: number
	breakdown: ScoreBreakdownItem[] | null
}

export type CandidateCardProps = {
	selectedCandidate?: CandidateModel
	campaign?: CampaignModel
	organisationId?: string
	candidatesLinkRenderer: (campaignId: string, props: LinkLocalProps) => ReactNode
	stages: Array<{
		id: string
		name: string
	}>
	formQuestions?: QuestionWithScoreData[]
	onStageChange: (candidateId: string, stageId: string) => void
	onPreviousCandidate?: () => void
	onNextCandidate?: () => void
	hasPreviousCandidate?: boolean
	hasNextCandidate?: boolean
	selectedEmailId?: string | null
	getEmailUrl?: (emailId: string) => string
}

function truncateAtWord(text: string, maxLength: number): string {
	if (text.length <= maxLength) { return text }
	const lastSpace = text.lastIndexOf(' ', maxLength)
	const cutoff = lastSpace > 0 ? lastSpace : maxLength
	return `${text.slice(0, cutoff)}…`
}

function getTabButtonClassName(isActive: boolean): string {
	return ctl(`
		flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors
		${
		isActive
			? 'border-brand-600 text-brand-600'
			: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
	}
	`)
}

function TabButton({
	isActive,
	onClick,
	children,
}: {
	isActive: boolean
	onClick: () => void
	children: ReactNode
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={getTabButtonClassName(isActive)}
		>
			{children}
		</button>
	)
}

function CountBadge({ count }: { count: number }) {
	if (count === 0) { return null }
	return (
		<span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
			{count}
		</span>
	)
}

function EmailIcon() {
	return (
		<svg xmlns="http://www.w3.org/2000/svg" width={14} height={14} viewBox="0 0 14 14" aria-hidden="true">
			<path
				d="M1.192 1.0316C1.0199 1.0689 0.942 1.0974 0.7952 1.1766C0.4098 1.3844 0.1476 1.7253 0.0358 2.1636L0 2.304V7V11.696L0.0358 11.8364C0.111 12.1313 0.2553 12.3838 0.4591 12.5771C0.6577 12.7654 0.8757 12.8883 1.1334 12.9571L1.264 12.992H7H12.736L12.8666 12.9571C13.4273 12.8074 13.8505 12.364 13.9709 11.8C13.9986 11.6704 14 11.4458 14 7C14 2.5542 13.9986 2.3296 13.9709 2.2C13.8701 1.728 13.557 1.3364 13.1183 1.1337C13.0523 1.1032 12.9429 1.0625 12.8752 1.0431L12.752 1.008L7.04 1.0051C1.5447 1.0022 1.3228 1.0032 1.192 1.0316M1.3237 2.0347C1.2271 2.0689 1.0922 2.1976 1.0461 2.2997C1.0082 2.3836 1.008 2.3872 1.0032 3.0585L0.9984 3.733L3.8764 5.7256C5.4594 6.8214 6.7863 7.731 6.8252 7.7468C6.9183 7.7847 7.0817 7.7847 7.1748 7.7468C7.2137 7.731 8.5406 6.8214 10.1236 5.7256L13.0016 3.733L12.9968 3.0585C12.992 2.3872 12.9918 2.3836 12.9539 2.2997C12.906 2.1938 12.7724 2.0687 12.6697 2.0337C12.601 2.0103 12.0965 2.0081 6.9932 2.0093C2.0409 2.0104 1.3841 2.0133 1.3237 2.0347M1.0038 8.292L1.008 11.616L1.0461 11.7003C1.094 11.8062 1.2276 11.9313 1.3303 11.9663C1.399 11.9897 1.905 11.992 7 11.992C12.095 11.992 12.601 11.9897 12.6697 11.9663C12.7724 11.9313 12.906 11.8062 12.9539 11.7003L12.992 11.616L12.9962 8.292C12.9985 6.4638 12.9968 4.968 12.9926 4.968C12.9883 4.968 11.8272 5.7694 10.4124 6.7489C8.9976 7.7284 7.7968 8.555 7.744 8.5856C7.5062 8.7238 7.2924 8.7795 7 8.7795C6.7076 8.7795 6.4938 8.7238 6.256 8.5856C6.2032 8.555 5.0024 7.7284 3.5876 6.7489C2.1728 5.7694 1.0117 4.968 1.0074 4.968C1.0032 4.968 1.0015 6.4638 1.0038 8.292"
				stroke="none"
				fill="currentColor"
				fillRule="evenodd"
				strokeWidth={0.016}
			/>
		</svg>
	)
}

type LeftPanelTabsProps = {
	candidateId: string
	emailCount: number
	whatsappCount: number
	whatsappEnabled: boolean
	jobillaMeetEnabled: boolean
	organisationId: string | undefined
	children: (leftTab: LeftPanelTab, setLeftTab: (tab: LeftPanelTab) => void) => ReactNode
}

export function LeftPanelTabs({
	candidateId,
	emailCount,
	whatsappCount,
	whatsappEnabled,
	jobillaMeetEnabled,
	organisationId,
	children,
}: LeftPanelTabsProps) {
	const { t } = useTranslation('common')
	const search = useSearch({
		from: '/_auth/company/$companyId/$campaignId/candidates/$candidateId',
	})
	const navigate = useNavigate()
	const { data: organisation } = useOrganisationRelationShow(organisationId ?? '', { enabled: !!organisationId })
	const { data: profile } = useCandidateProfileShow(candidateId, { enabled: !!candidateId })
	const hasProfileEntries = !!profile
		&& profile.workHistory.length + profile.education.length + profile.licenses.length > 0
	const profileTabVisible = organisation?.candidateProfileEnabled !== false || hasProfileEntries
	// 'interviews' is a right-panel tab, not a left-panel one — see RightPanelTabs. 'meeting'
	// and 'profile' fall back too when their tab is hidden, so a stale deep-link can't strand the panel.
	const rawTab = search.tab ?? 'responses'
	const leftTab: LeftPanelTab = rawTab === 'interviews'
			|| (rawTab === 'meeting' && !jobillaMeetEnabled)
			|| (rawTab === 'profile' && !profileTabVisible)
		? 'responses'
		: rawTab

	const setLeftTab = useCallback((next: LeftPanelTab) => {
		navigate({
			to: '.',
			search: (prev) => ({ ...prev, tab: next }),
			replace: true,
		})
	}, [navigate])

	return (
		<div className="flex flex-col h-full bg-white">
			<div className="flex items-center gap-1 px-4 border-b border-gray-200 shrink-0">
				<TabButton
					isActive={leftTab === 'responses'}
					onClick={() => setLeftTab('responses')}
				>
					<ClipboardListIcon className="size-3.5" aria-hidden="true" />
					{t('candidateCard.tabs.responses')}
				</TabButton>
				{profileTabVisible
					? (
						<TabButton
							isActive={leftTab === 'profile'}
							onClick={() => setLeftTab('profile')}
						>
							<UserIcon className="size-3.5" aria-hidden="true" />
							{t('candidateCard.tabs.profile')}
							<ProfileSectionCounts candidateId={candidateId} />
						</TabButton>
					)
					: null}
				<TabButton
					isActive={leftTab === 'emails'}
					onClick={() => setLeftTab('emails')}
				>
					<EmailIcon />
					{t('candidateCard.tabs.emails')}
					<CountBadge count={emailCount} />
				</TabButton>
				{whatsappEnabled && organisationId && (
					<TabButton
						isActive={leftTab === 'whatsapp'}
						onClick={() => setLeftTab('whatsapp')}
					>
						<MessageCircleIcon className="size-3.5" aria-hidden="true" />
						{t('candidateCard.tabs.whatsapp')}
						<CountBadge count={whatsappCount} />
					</TabButton>
				)}
				{jobillaMeetEnabled && (
					<TabButton
						isActive={leftTab === 'meeting'}
						onClick={() => setLeftTab('meeting')}
					>
						<VideoIcon className="size-3.5" aria-hidden="true" />
						{t('candidateCard.tabs.meeting')}
					</TabButton>
				)}
			</div>

			<div className="flex-1 overflow-hidden">
				{children(leftTab, setLeftTab)}
			</div>
		</div>
	)
}

function InfoRow({
	label,
	children,
}: {
	label: string
	children: ReactNode
}) {
	return (
		<div className="flex items-center gap-3">
			<span className="text-sm font-medium text-gray-500 w-16">{label}</span>
			{children}
		</div>
	)
}

type DisqualifyingResponseInfo = { index: number; questionNumber: number; question: string; answers: string[] }

function DisqualificationBanner({
	disqualifyingResponses,
	onDisqualificationClick,
}: {
	disqualifyingResponses: DisqualifyingResponseInfo[]
	onDisqualificationClick: (responseIndex: number) => void
}) {
	const { t } = useTranslation('common')
	const disqualificationLinkClasses =
		'text-red-600 hover:text-red-800 cursor-pointer underline decoration-dotted decoration-red-400 underline-offset-2'

	const reasonLabel = (disqualification: DisqualifyingResponseInfo) => `Q${disqualification.questionNumber}`

	const reasonTooltip = (disqualification: DisqualifyingResponseInfo) =>
		t('candidateCard.disqualification.reason', {
			answer: disqualification.answers.map(a => `"${a}"`).join(', '),
			question: disqualification.question,
			questionNumber: disqualification.questionNumber,
		})

	if (disqualifyingResponses.length === 0) {
		return (
			<div className="mb-4 px-3 py-2 text-sm bg-red-50 text-red-800 border border-red-200 rounded-lg">
				<span className="font-medium">{t('candidateCard.disqualification.title')}</span>
				{' ('}
				<Tooltip wrapperClassName="inline" content={t('candidateCard.disqualification.formEdited')}>
					<span className={`${disqualificationLinkClasses} cursor-help`}>
						{t('candidateCard.disqualification.seeReason')}
					</span>
				</Tooltip>
				{')'}
			</div>
		)
	}

	return (
		<div className="mb-4 px-3 py-2 text-sm bg-red-50 text-red-800 border border-red-200 rounded-lg">
			<span className="font-medium">{t('candidateCard.disqualification.title')}</span>
			{' ('}
			{disqualifyingResponses.map((disqualification, disqualificationIndex) => (
				<span key={disqualification.index}>
					{disqualificationIndex > 0 && ', '}
					<Tooltip wrapperClassName="inline" content={reasonTooltip(disqualification)}>
						<button
							type="button"
							onClick={() => onDisqualificationClick(disqualification.index)}
							className={disqualificationLinkClasses}
						>
							{reasonLabel(disqualification)}
						</button>
					</Tooltip>
				</span>
			))}
			{')'}
		</div>
	)
}

export function CandidateCard(
	{
		selectedCandidate,
		candidatesLinkRenderer,
		campaign,
		organisationId,
		stages,
		formQuestions,
		onStageChange,
		onPreviousCandidate,
		onNextCandidate,
		hasPreviousCandidate = false,
		hasNextCandidate = false,
		selectedEmailId,
		getEmailUrl,
	}: CandidateCardProps,
) {
	const { t } = useTranslation('common')
	const whatsappEnabled = useWhatsAppEnabled()
	const queryClient = useQueryClient()
	const fileInputRef = useRef<HTMLInputElement>(null)

	// A notification (interview_booked/interview_cancelled/interview_stage_move_failed)
	// deep-links here with ?tab=interviews to open the right-panel Interviews tab.
	const search = useSearch({ from: '/_auth/company/$companyId/$campaignId/candidates/$candidateId' })
	const { companyId } = useParams({ from: '/_auth/company/$companyId/$campaignId/candidates/$candidateId' })
	const { can, isLoading: entitlementsLoading } = useEntitlements()
	const jobillaMeetEnabled = !entitlementsLoading && can(stripeFeature['jobilla-meet'])
	const navigate = useNavigate()
	const openMeetingTab = useCallback(() => {
		navigate({ to: '.', search: (prev) => ({ ...prev, tab: 'meeting' }), replace: true })
	}, [navigate])
	// A live Jobilla Meet call is owned here (not inside the tab) so it survives left-tab
	// switches — docked in the Jobilla Meet tab, a corner popout on the other tabs.
	const [activeCall, setActiveCall] = useAtom(activeCallAtom, { store: callStore })
	// Only honour a ?tab=interviews deep-link when the feature is actually on — the
	// tab button and its panel are both flag-gated, so opening it with the flag off
	// would leave the right panel blank with no active tab and no way back.
	const [rightTab, setRightTab] = useState<RightPanelTab>(
		search.tab === 'interviews' ? 'interviews' : 'overview',
	)
	const [sendDialogOpen, setSendDialogOpen] = useState(false)
	// The flag can resolve after mount; honour the deep-link once it does, but only
	// once, so it never overrides the recruiter's own later tab navigation.
	const honouredInterviewDeepLink = useRef(search.tab === 'interviews')
	useEffect(() => {
		if (!honouredInterviewDeepLink.current && search.tab === 'interviews') {
			honouredInterviewDeepLink.current = true
			setRightTab('interviews')
		}
	}, [search.tab])

	const currentStage = stages.find((stage) => stage.id === selectedCandidate?.stageId)

	const handleStageChange = useCallback((stageId: string) => {
		if (!selectedCandidate) { return }

		const stage = stages.find((s) => s.id === stageId)
		if (!stage) {
			logger.error(new Error(`Stage ${stageId} not found, unable to move candidate`), {
				tags: { feature: 'pipeline' },
				data: {
					stageId,
					candidateId: selectedCandidate.id,
					currentStageId: currentStage?.id,
					currentStageName: currentStage?.name,
					availableStageIds: stages.map(s => s.id),
				},
			})
			return
		}

		logger.track('candidates:candidate_move_stage', {
			data: {
				trigger: 'card_badge',
				candidate_id: selectedCandidate.id,
				from_stage: currentStage?.name,
				to_stage: stage.name,
			},
		})

		onStageChange(selectedCandidate.id, stageId)
	}, [selectedCandidate, onStageChange, currentStage, stages])

	const handleQuickReject = useCallback(() => {
		const rejectedStage = stages.find(s => s.name.toLowerCase() === 'rejected')
		if (rejectedStage) {
			handleStageChange(rejectedStage.id)
		}
	}, [handleStageChange, stages])

	const handleMarkAsHired = useCallback(() => {
		const hiredStage = stages.find(s => s.name.toLowerCase() === 'hired')
		if (hiredStage) {
			handleStageChange(hiredStage.id)
		}
	}, [handleStageChange, stages])

	if (!campaign) {
		throw new Error('CandidateCard requires a campaign prop')
	}

	const candidateId = selectedCandidate?.id ?? ''

	const { emails: candidateEmails } = useCandidateEmails(candidateId, campaign.id)
	const emailCount = candidateEmails.length

	const whatsappEnabledForOrg = whatsappEnabled && Boolean(organisationId) && Boolean(candidateId)
	const { messages: whatsappMessages } = useCandidateWhatsApp(
		whatsappEnabledForOrg ? candidateId : '',
		whatsappEnabledForOrg ? campaign.id : undefined,
	)
	const whatsappCount = whatsappEnabledForOrg ? whatsappMessages.length : 0

	const candidateFilesQuery = useCandidateFileIndex(candidateId, {
		enabled: Boolean(candidateId),
	})
	const candidateFiles = candidateFilesQuery.data ?? []
	const cvFiles = candidateFiles.filter((candidateFile) => candidateFile.attribute === 'cv')

	usePrivateChannel(
		organisationId && candidateId
			? broadcastChannel.OrganisationCandidateFile({ organisation: organisationId })
			: null,
		{ [candidateFileEvent.Scanned]: [candidateFileIndexQueryKey(candidateId)] },
	)

	usePrivateChannel(
		organisationId && candidateId
			? broadcastChannel.OrganisationCandidateProfile({ organisation: organisationId })
			: null,
		{ [candidateProfileEvent.Updated]: [candidateProfileShowQueryKey(candidateId)] },
	)
	const candidateFileUploadMutation = useCandidateFileStore({
		mutationKey: ['candidate-file-upload', candidateId],
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: candidateFileIndexQueryKey(candidateId) })
		},
	})

	const fileTagMutation = useMutation<
		unknown,
		AxiosError<Partial<CandidateFileTagUpdate422>>,
		{ fileId: string; attribute: MediaAttribute | null }
	>({
		mutationFn: ({ fileId, attribute }) =>
			attribute === null
				? candidateFileTagDestroy(candidateId, fileId)
				: candidateFileTagUpdate(candidateId, fileId, { attribute }),
		onSuccess: () =>
			Promise.all([
				queryClient.invalidateQueries({ queryKey: candidateFileIndexQueryKey(candidateId) }),
				queryClient.invalidateQueries({ queryKey: pipelineStageShowQueryKey(campaign.id) }),
			]),
		onError: (error, { fileId, attribute }) => {
			const status = error.response?.status
			if (status === 401 || status === 403) {
				return
			}

			const context = { candidateId, fileId, attribute }
			if (error.response?.data.errors?.attribute?.includes(candidateFileTagRequiresCleanScan)) {
				logger.warn('Candidate file tag rejected until the security scan passes', {
					category: 'api',
					data: context,
				})
				return
			}

			logger.error(error, { tags: { feature: 'candidate-files' }, data: context })
		},
	})

	const fileDestroyMutation = useCandidateFileDestroy({
		onSuccess: () =>
			Promise.all([
				queryClient.invalidateQueries({ queryKey: candidateFileIndexQueryKey(candidateId) }),
				queryClient.invalidateQueries({ queryKey: pipelineStageShowQueryKey(campaign.id) }),
			]),
		onError: (error, { fileId }) => {
			const status = error.response?.status
			if (status === 401 || status === 403) {
				return
			}

			logger.error(error, { tags: { feature: 'candidate-files' }, data: { candidateId, fileId } })
		},
	})

	const fileDeleteConfirmRef = useRef<ConfirmationDialogRef>(null)

	const handleFileDelete = async (file: CandidateFileData) => {
		try {
			await fileDeleteConfirmRef.current?.confirm({
				title: t('candidateFiles.deleteTitle', { name: file.name }),
				message: t('candidateFiles.deleteMessage'),
				confirmText: t('candidateFiles.deleteConfirm'),
				cancelText: t('common.cancel'),
				destructive: true,
			})
		} catch {
			// cancelled
			return
		}
		fileDestroyMutation.mutate({ candidateId, fileId: file.id })
	}

	const handleFileUploadClick = useCallback(() => {
		fileInputRef.current?.click()
	}, [])

	const handleFileInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		if (file) {
			candidateFileUploadMutation.mutate({ candidateId, data: { file } })
		}
		event.target.value = ''
	}, [candidateFileUploadMutation, candidateId])

	const scoreBreakdown = selectedCandidate?.score?.breakdown ?? []
	const responsesWithScoring = useMemo(() => {
		if (!selectedCandidate) { return [] }

		return selectedCandidate.response.map((response) => {
			const matchingFormQuestion = formQuestions?.find(formQuestion =>
				response.options?.some(responseOption =>
					formQuestion.options?.some(formOption => formOption.id === responseOption.id)
				)
			)
			const scoreContribution = matchingFormQuestion?.id
				? scoreBreakdown.find(entry => entry.questionId === matchingFormQuestion.id)
				: undefined
			const scoredOptions: ScoredOption[] | undefined = response.options?.map(responseOption => {
				const formOption = matchingFormQuestion?.options?.find(option => option.id === responseOption.id)
				return {
					...responseOption,
					scoreValue: formOption?.scoreValue ?? null,
					isDisqualifying: formOption?.isDisqualifying ?? null,
				}
			})

			return {
				...response,
				scoredOptions,
				scoreContribution,
			}
		})
	}, [selectedCandidate, scoreBreakdown, formQuestions])

	const disqualifyingResponses = useMemo(() => {
		const results: DisqualifyingResponseInfo[] = []
		for (const [index, response] of responsesWithScoring.entries()) {
			const selectedAnswers = response.answer
			const disqualifyingOptions = response.scoredOptions?.filter(option =>
				(selectedAnswers.some(a =>
					a.answer == option.label || a.optionId == option.id
				))
				&& option.isDisqualifying === true
			) ?? []
			if (disqualifyingOptions.length > 0) {
				const truncatedQuestion = truncateAtWord(response.question, 30)
				const answers = disqualifyingOptions.map(option => truncateAtWord(option.label, 40))
				results.push({ index, questionNumber: index + 1, question: truncatedQuestion, answers })
			}
		}
		return results
	}, [responsesWithScoring])

	const disqualificationRefs = useRef<Map<number, HTMLDivElement>>(new Map())
	const [highlightedDisqualificationIndex, setHighlightedDisqualificationIndex] = useState<number | null>(null)

	const handleDisqualificationBannerClick = useCallback(
		(responseIndex: number) => {
			setHighlightedDisqualificationIndex(responseIndex)
			setTimeout(() => {
				disqualificationRefs.current.get(responseIndex)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
			}, 50)
			setTimeout(() => setHighlightedDisqualificationIndex(null), 2000)
		},
		[],
	)

	if (!selectedCandidate) {
		return null
	}

	const currentStageLowercase = (currentStage?.name?.toLowerCase() ?? 'new') as
		| 'new'
		| 'call'
		| 'interview'
		| 'offer'
		| 'hired'
		| 'rejected'

	// Separate rejected stage from other stages for the menu
	const nonRejectedStages = stages.filter(s => s.name.toLowerCase() !== 'rejected')
	const rejectedStage = stages.find(s => s.name.toLowerCase() === 'rejected')

	const leftPanel = (
		<LeftPanelTabs
			candidateId={candidateId}
			emailCount={emailCount}
			whatsappCount={whatsappCount}
			whatsappEnabled={whatsappEnabled}
			jobillaMeetEnabled={jobillaMeetEnabled}
			organisationId={organisationId}
		>
			{(leftTab, setLeftTab) => (
				<>
					<CandidateTabOpenedTracker
						eventName="Candidate: Tab Opened"
						tab={leftTab}
						candidate={selectedCandidate}
					/>
					{leftTab === 'responses' && (
						<div className="h-full overflow-auto p-4">
							{selectedCandidate.disqualificationReason && (
								<DisqualificationBanner
									disqualifyingResponses={disqualifyingResponses}
									onDisqualificationClick={(responseIndex) => {
										setLeftTab('responses')
										handleDisqualificationBannerClick(responseIndex)
									}}
								/>
							)}
							<div className="divide-y divide-gray-100">
								{responsesWithScoring.map((response, index) => (
									<ApplicationResponseItem
										key={response.id}
										questionNumber={index + 1}
										question={response.question}
										answer={response.answer}
										options={response.scoredOptions}
										scoreContribution={response.scoreContribution}
										isHighlighted={highlightedDisqualificationIndex === index}
										responseRef={(el: HTMLDivElement | null) => {
											if (el) { disqualificationRefs.current.set(index, el) }
											else { disqualificationRefs.current.delete(index) }
										}}
									/>
								))}
								{selectedCandidate.response.length === 0 && (
									<p className="text-sm text-gray-400 text-center py-8">
										{t('candidateCard.noResponses')}
									</p>
								)}
								<CandidateAdditionalAnswers questions={selectedCandidate.additionalQuestions ?? []} />
							</div>
						</div>
					)}

					{leftTab === 'profile' && <CandidateProfileTab candidateId={candidateId} />}

					{leftTab === 'emails' && (
						<EmailThread
							candidate={selectedCandidate}
							campaignId={campaign.id}
							selectedEmailId={selectedEmailId}
							getEmailUrl={getEmailUrl}
						/>
					)}

					{organisationId && whatsappEnabled && (
						<div className={leftTab !== 'whatsapp' ? 'hidden' : 'h-full'}>
							<WhatsAppThread
								candidate={selectedCandidate}
								campaignId={campaign.id}
								organisationId={organisationId}
								isActive={leftTab === 'whatsapp'}
							/>
						</div>
					)}
					{leftTab === 'meeting' && (
						<JobillaMeetTab
							candidateId={candidateId}
							campaignId={campaign.id}
							hasActiveCall={activeCall?.candidateId === candidateId}
							onStartCall={(joinUrl) =>
								setActiveCall({ joinUrl, companyId, campaignId: campaign.id, candidateId })}
						/>
					)}
				</>
			)}
		</LeftPanelTabs>
	)

	const rightPanel = (
		<div className="flex flex-col h-full bg-white">
			<div className="flex items-center gap-1 px-4 border-b border-gray-200 shrink-0">
				<TabButton
					isActive={rightTab === 'overview'}
					onClick={() => setRightTab('overview')}
				>
					<UserIcon className="size-3.5" aria-hidden="true" />
					{t('candidateCard.tabs.overview')}
				</TabButton>
				<TabButton
					isActive={rightTab === 'interviews'}
					onClick={() => setRightTab('interviews')}
				>
					<CalendarIcon className="size-3.5" aria-hidden="true" />
					{t('candidateCard.tabs.interviews')}
				</TabButton>
				{selectedCandidate.submissionCount != null && selectedCandidate.submissionCount > 1 && (
					<TabButton
						isActive={rightTab === 'history'}
						onClick={() => setRightTab('history')}
					>
						<HistoryIcon className="size-3.5" aria-hidden="true" />
						{t('candidateCard.tabs.history')}
						<CountBadge count={selectedCandidate.submissionCount} />
					</TabButton>
				)}
				<TabButton
					isActive={rightTab === 'files'}
					onClick={() => setRightTab('files')}
				>
					<PaperclipIcon className="size-3.5" aria-hidden="true" />
					{t('candidateCard.tabs.files')}
					<CountBadge count={candidateFiles.length} />
				</TabButton>
			</div>

			<div className="flex-1 overflow-hidden">
				<CandidateTabOpenedTracker
					eventName="Candidate Side View: Tab Opened"
					tab={rightTab}
					candidate={selectedCandidate}
				/>
				{rightTab === 'overview' && (
					<div className="h-full overflow-auto p-4 space-y-4">
						{/* Status */}
						<InfoRow label={t('candidateCard.info.status')}>
							<div className="flex items-center gap-2">
								<Menu as="div" className="relative">
									<MenuButton className="cursor-pointer hover:opacity-80 transition-opacity">
										<PipelineBadge stage={currentStageLowercase} name={currentStage?.name} />
									</MenuButton>
									<MenuItems className="absolute left-0 mt-1 w-36 bg-white rounded-lg shadow-lg ring-1 ring-black/5 py-1 z-50">
										{nonRejectedStages.map((stage) => (
											<MenuItem key={stage.id}>
												<button
													type="button"
													onClick={() => handleStageChange(stage.id)}
													className="flex items-center justify-between w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
												>
													{stage.name}
													{currentStage?.id === stage.id && (
														<CheckIcon className="size-4 text-brand-600" />
													)}
												</button>
											</MenuItem>
										))}
										{rejectedStage && (
											<>
												<div className="border-t border-gray-100 my-1" />
												<MenuItem>
													<button
														type="button"
														onClick={() => handleStageChange(rejectedStage.id)}
														className="flex items-center justify-between w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50"
													>
														{rejectedStage.name}
														{currentStage?.id === rejectedStage.id && (
															<CheckIcon className="size-4" />
														)}
													</button>
												</MenuItem>
											</>
										)}
									</MenuItems>
								</Menu>

								<button
									type="button"
									onClick={handleQuickReject}
									disabled={currentStage?.name?.toLowerCase() === 'rejected'}
									className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
								>
									<XIcon className="size-3" />
									{t('candidateCard.actions.reject')}
								</button>
								<button
									type="button"
									onClick={handleMarkAsHired}
									disabled={currentStage?.name?.toLowerCase() === 'hired'}
									className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-brand-600 hover:bg-brand-50 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
								>
									<ArrowRightIcon className="size-3" />
									{t('candidateCard.actions.hired')}
								</button>
							</div>
						</InfoRow>

						{/* Phone */}
						{selectedCandidate.phone && (
							<InfoRow label={t('candidateCard.info.phone')}>
								<a
									href={`tel:${selectedCandidate.phone}`}
									className="ph-no-capture text-sm text-brand-600 hover:text-brand-700 hover:underline"
								>
									{selectedCandidate.phone}
								</a>
							</InfoRow>
						)}

						{/* Email */}
						{selectedCandidate.email && (
							<InfoRow label={t('candidateCard.info.email')}>
								<a
									href={`mailto:${selectedCandidate.email}`}
									className="ph-no-capture text-sm text-brand-600 hover:text-brand-700 hover:underline"
								>
									{selectedCandidate.email}
								</a>
							</InfoRow>
						)}

						{/* Applied date */}
						{selectedCandidate.createdAt && (
							<InfoRow label={t('candidateCard.info.applied')}>
								<span className="text-sm text-gray-700">
									<TimeAgo date={selectedCandidate.createdAt} />
								</span>
							</InfoRow>
						)}

						{cvFiles.length > 0 && (
							<InfoRow label={t('candidateFiles.cv')}>
								<div className="flex flex-1 min-w-0 flex-col gap-2">
									{cvFiles.map((cvFile) => <CvFileItem key={cvFile.id} file={cvFile} />)}
								</div>
							</InfoRow>
						)}

						<NextInterviewPanel
							candidateId={selectedCandidate.id}
							interviewBooking={selectedCandidate.interviewBooking}
							onJoinInApp={jobillaMeetEnabled ? openMeetingTab : undefined}
						/>

						{/* Notes */}
						<div className="mt-6">
							<CandidateInternalNotes
								candidateId={selectedCandidate.id}
								candidateName={selectedCandidate.name}
								campaignId={campaign.id}
							/>
						</div>
					</div>
				)}

				{rightTab === 'interviews' && (
					<div className="h-full overflow-auto p-4">
						<InterviewsTab
							candidateId={candidateId}
							campaignId={campaign.id}
							onSendBookingLink={() => setSendDialogOpen(true)}
							onJoinInApp={jobillaMeetEnabled ? openMeetingTab : undefined}
						/>
					</div>
				)}

				{rightTab === 'history' && (
					<div className="h-full overflow-auto p-4">
						<ApplicationHistory candidateId={selectedCandidate.id} formQuestions={formQuestions} />
					</div>
				)}

				{rightTab === 'files' && (
					<div className="h-full overflow-auto p-4">
						<EditorErrorBoundary debugId="candidate-card-files">
							<CandidateFiles
								files={candidateFiles}
								getEmailUrl={getEmailUrl}
								onUpload={handleFileUploadClick}
								onTagChange={(file, attribute) =>
									fileTagMutation.mutate({ fileId: file.id, attribute })}
								savingTagFileId={fileTagMutation.isPending ? fileTagMutation.variables.fileId : null}
								tagFailure={fileTagMutation.isError
									? {
										fileId: fileTagMutation.variables.fileId,
										requiresCleanScan: fileTagMutation.error.response?.data.errors?.attribute
											?.includes(candidateFileTagRequiresCleanScan) ?? false,
									}
									: null}
								onDelete={(file) => void handleFileDelete(file)}
								deletingFileId={fileDestroyMutation.isPending
									? fileDestroyMutation.variables.fileId
									: null}
								deleteFailedFileId={fileDestroyMutation.isError
									? fileDestroyMutation.variables.fileId
									: null}
							/>
							<input
								ref={fileInputRef}
								type="file"
								onChange={handleFileInputChange}
								className="hidden"
							/>
							<ConfirmationDialog ref={fileDeleteConfirmRef} />
						</EditorErrorBoundary>
					</div>
				)}
			</div>
		</div>
	)

	const closeButton = candidatesLinkRenderer(campaign.id, {
		children: (
			<div className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all cursor-pointer">
				<XIcon className="size-5" />
			</div>
		),
	})

	return (
		<>
			<ResizablePanelLayout
				candidateName={selectedCandidate.name}
				score={selectedCandidate.score}
				disqualificationReason={selectedCandidate.disqualificationReason}
				leftPanel={leftPanel}
				rightPanel={rightPanel}
				closeButton={closeButton}
				onPreviousCandidate={onPreviousCandidate}
				onNextCandidate={onNextCandidate}
				hasPreviousCandidate={hasPreviousCandidate}
				hasNextCandidate={hasNextCandidate}
				isNavigationDisabled={false}
			/>
			<SendBookingLinkDialog
				isOpen={sendDialogOpen}
				onClose={() => setSendDialogOpen(false)}
				campaignId={campaign.id}
				candidateIds={[candidateId]}
				candidateName={selectedCandidate.name}
				isBulk={false}
			/>
		</>
	)
}

type ResizablePanelLayoutProps = {
	candidateName: string
	score?: CandidateScore | null
	disqualificationReason?: string | null
	leftPanel: ReactNode
	rightPanel: ReactNode
	closeButton: ReactNode
	onPreviousCandidate?: () => void
	onNextCandidate?: () => void
	hasPreviousCandidate: boolean
	hasNextCandidate: boolean
	isNavigationDisabled: boolean
}

function ResizablePanelLayout({
	candidateName,
	score,
	disqualificationReason,
	leftPanel,
	rightPanel,
	closeButton,
	onPreviousCandidate,
	onNextCandidate,
	hasPreviousCandidate,
	hasNextCandidate,
	isNavigationDisabled,
}: ResizablePanelLayoutProps) {
	const { t } = useTranslation('common')
	const [leftPanelPercent, setLeftPanelPercent] = useState<number>(PANEL_CONFIG.defaultLeftPercent)
	const containerRef = useRef<HTMLDivElement>(null)

	const handlePanelDrag = useCallback((_: unknown, info: PanInfo) => {
		const container = containerRef.current
		if (!container) { return }

		const containerRect = container.getBoundingClientRect()
		const newPercent = ((info.point.x - containerRect.left) / containerRect.width) * 100
		const clampedPercent = Math.min(
			PANEL_CONFIG.maxPercent,
			Math.max(PANEL_CONFIG.minPercent, newPercent),
		)
		setLeftPanelPercent(clampedPercent)
	}, [])

	const handleResetPanelSize = useCallback(() => {
		setLeftPanelPercent(PANEL_CONFIG.defaultLeftPercent)
	}, [])

	return (
		<div className="relative h-full flex flex-col">
			{/* Header */}
			<div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
				<div className="flex items-center gap-3">
					<h1 className="ph-no-capture text-lg font-semibold text-gray-900">
						{candidateName}
					</h1>
					{disqualificationReason
						? (
							<span className="px-2.5 py-1 text-sm font-medium rounded-full bg-red-600 text-white">
								{score
									? `${t('candidateCard.disqualification.title')} (${Math.round(score.percentage)}%)`
									: t('candidateCard.disqualification.title')}
							</span>
						)
						: score && <ScoreBadge percentage={score.percentage} />}
				</div>
				<div className="flex items-center gap-1">
					<button
						type="button"
						onClick={onPreviousCandidate}
						disabled={!hasPreviousCandidate || isNavigationDisabled}
						className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all disabled:opacity-30 disabled:cursor-not-allowed"
						title={t('candidateCard.navigation.previous')}
					>
						<ChevronUpIcon className="size-5" />
					</button>
					<button
						type="button"
						onClick={onNextCandidate}
						disabled={!hasNextCandidate || isNavigationDisabled}
						className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all disabled:opacity-30 disabled:cursor-not-allowed"
						title={t('candidateCard.navigation.next')}
					>
						<ChevronDownIcon className="size-5" />
					</button>
					{closeButton}
				</div>
			</div>

			{/* Resizable panels */}
			<div
				ref={containerRef}
				className="relative flex-1 grid min-h-0"
				style={{
					gridTemplateColumns: `${leftPanelPercent}% ${100 - leftPanelPercent}%`,
				}}
			>
				<div className="h-full overflow-hidden border-r border-gray-200">
					{leftPanel}
				</div>

				{/* Drag handle */}
				<motion.div
					className="absolute inset-y-0 w-4 cursor-col-resize z-20 select-none touch-none flex items-center justify-center group"
					style={{ left: `calc(${leftPanelPercent}% - 8px)` }}
					onPan={handlePanelDrag}
					onDoubleClick={handleResetPanelSize}
				>
					<div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
						<div className="w-0.5 h-8 rounded-full bg-gray-300 group-hover:bg-gray-400 group-active:bg-brand-500 transition-colors" />
						<div className="w-0.5 h-8 rounded-full bg-gray-300 group-hover:bg-gray-400 group-active:bg-brand-500 transition-colors" />
					</div>
				</motion.div>

				<div className="h-full overflow-hidden">
					{rightPanel}
				</div>
			</div>
		</div>
	)
}
